import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  onboardingControllerAddDocumentVersionV1,
  onboardingControllerCancelV1,
  onboardingControllerCreateDocumentV1,
  onboardingControllerFindOneV1,
  onboardingControllerListV1,
  onboardingControllerRemoveDocumentV1,
  onboardingControllerReplaceCareerV1,
  onboardingControllerRunIdentityV1,
  onboardingControllerStartRepresentationContractV1,
  onboardingControllerStartV1,
  onboardingControllerUpdateIdentityV1,
  onboardingControllerUpdateProfileV1,
} from '@/api/generated/onboarding/onboarding';
import { taskKeys } from '@/modules/tasks';
import { tasksControllerUpdateTaskV1 } from '@/api/generated/tasks/tasks';
import { auditLogKeys } from '@/shared/lib/auditLogKeys';
import { contractKeys } from '@/shared/lib/contractKeys';
import type {
  AddDocumentVersionDto,
  CreateDocumentDto,
  OnboardingDetailDto,
  ReplaceCareerDto,
  StartRepresentationContractDto,
  UpdateIdentityCheckDto,
  UpdateOnboardingProfileDto,
  UpdateTaskDto,
} from '@/api/generated/model';
import { playerKeys } from '@/modules/players';

/**
 * Query keys for the onboarding module.
 *
 * `detail` is the only read the screen makes: the API returns tasks, identity,
 * the representation contract, documents and career history in one response, so there is one
 * cache entry per onboarding rather than five that can disagree with each
 * other mid-render.
 */
export const onboardingKeys = {
  all: ['onboardings'] as const,
  lists: () => [...onboardingKeys.all, 'list'] as const,
  list: () => [...onboardingKeys.lists(), 'default'] as const,
  details: () => [...onboardingKeys.all, 'detail'] as const,
  detail: (id: string) => [...onboardingKeys.details(), id] as const,
  /** The shared audit panel owns this query; this just names its key. */
  audit: (id: string) => auditLogKeys.forSubject('player_onboarding', id),
};

export function useOnboardingsQuery() {
  return useQuery({
    queryKey: onboardingKeys.list(),
    queryFn: ({ signal }) => onboardingControllerListV1({ limit: 50 }, { signal }),
  });
}

export function useOnboardingQuery(id: string | undefined) {
  return useQuery({
    queryKey: onboardingKeys.detail(id ?? ''),
    queryFn: ({ signal }) => onboardingControllerFindOneV1(id as string, { signal }),
    enabled: Boolean(id),
  });
}

/**
 * Shared success handling for every onboarding write.
 *
 * All of them return the complete `OnboardingDetailDto`, so the response is
 * written straight into the detail cache — no refetch, and the screen never
 * flickers through a loading state after a save. The audit list is invalidated
 * because every one of these appends to it.
 */
function useOnboardingMutationEffects() {
  const queryClient = useQueryClient();

  return async (detail: OnboardingDetailDto) => {
    queryClient.setQueryData(onboardingKeys.detail(detail.id), detail);
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: onboardingKeys.lists() }),
      queryClient.invalidateQueries({ queryKey: onboardingKeys.audit(detail.id) }),
    ]);
  };
}

export function useStartOnboardingMutation() {
  const queryClient = useQueryClient();
  const onDetail = useOnboardingMutationEffects();

  return useMutation({
    mutationFn: (playerId: string) => onboardingControllerStartV1({ playerId }),
    onSuccess: async (detail) => {
      await onDetail(detail);
      // The players list shows who is being onboarded, once that column exists.
      await queryClient.invalidateQueries({ queryKey: playerKeys.lists() });
    },
  });
}

export function useCancelOnboardingMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => onboardingControllerCancelV1(id),
    onSuccess: async (_result, id) => {
      // Dropped rather than invalidated: refetching an onboarding that no
      // longer exists only produces a 404.
      queryClient.removeQueries({ queryKey: onboardingKeys.detail(id) });
      await queryClient.invalidateQueries({ queryKey: onboardingKeys.lists() });
    },
  });
}

/**
 * Task changes go through the generic tasks endpoint, because a task is a task
 * whether or not it belongs to a checklist. It returns the task rather than the
 * whole onboarding, so the detail is invalidated instead of written.
 */
export function useUpdateTaskMutation(onboardingId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, dto }: { id: string; dto: UpdateTaskDto }) =>
      tasksControllerUpdateTaskV1(id, dto),
    onSuccess: async () => {
      await Promise.all([
        // Completing a step changes the lock state of the next one and the
        // completion percentage, so the whole detail is refetched.
        queryClient.invalidateQueries({ queryKey: onboardingKeys.detail(onboardingId) }),
        queryClient.invalidateQueries({ queryKey: onboardingKeys.lists() }),
        queryClient.invalidateQueries({ queryKey: onboardingKeys.audit(onboardingId) }),
        // Checklist steps are tasks, so the Tasks board shows them too.
        queryClient.invalidateQueries({ queryKey: taskKeys.all }),
      ]);
    },
  });
}

export function useUpdateProfileMutation(id: string) {
  const onDetail = useOnboardingMutationEffects();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dto: UpdateOnboardingProfileDto) => onboardingControllerUpdateProfileV1(id, dto),
    onSuccess: async (detail) => {
      await onDetail(detail);
      // These fields live on the player row, so a cached player detail is stale.
      queryClient.removeQueries({ queryKey: playerKeys.detail(detail.player.id) });
    },
  });
}

export function useReplaceCareerMutation(id: string) {
  const onDetail = useOnboardingMutationEffects();
  return useMutation({
    mutationFn: (dto: ReplaceCareerDto) => onboardingControllerReplaceCareerV1(id, dto),
    onSuccess: onDetail,
  });
}

export function useUpdateIdentityMutation(id: string) {
  const onDetail = useOnboardingMutationEffects();
  return useMutation({
    mutationFn: (dto: UpdateIdentityCheckDto) => onboardingControllerUpdateIdentityV1(id, dto),
    onSuccess: onDetail,
  });
}

export function useRunIdentityMutation(id: string) {
  const onDetail = useOnboardingMutationEffects();
  return useMutation({
    mutationFn: () => onboardingControllerRunIdentityV1(id),
    onSuccess: onDetail,
  });
}

/**
 * Step 4: opens the player's representation contract in the contracts
 * inventory. Uploading the signed PDF and marking it executed happen through
 * the contracts module's own mutations; the onboarding is refetched because
 * the step's task and the completion percentage both move.
 */
export function useStartRepresentationContractMutation(id: string) {
  const onDetail = useOnboardingMutationEffects();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (dto: StartRepresentationContractDto) =>
      onboardingControllerStartRepresentationContractV1(id, dto),
    onSuccess: async (detail) => {
      await onDetail(detail);
      await queryClient.invalidateQueries({ queryKey: contractKeys.lists() });
    },
  });
}

export function useCreateDocumentMutation(id: string) {
  const onDetail = useOnboardingMutationEffects();
  return useMutation({
    mutationFn: (dto: CreateDocumentDto) => onboardingControllerCreateDocumentV1(id, dto),
    onSuccess: onDetail,
  });
}

export function useAddDocumentVersionMutation(id: string) {
  const onDetail = useOnboardingMutationEffects();
  return useMutation({
    mutationFn: ({ documentId, dto }: { documentId: string; dto: AddDocumentVersionDto }) =>
      onboardingControllerAddDocumentVersionV1(id, documentId, dto),
    onSuccess: onDetail,
  });
}

export function useRemoveDocumentMutation(id: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (documentId: string) => onboardingControllerRemoveDocumentV1(id, documentId),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: onboardingKeys.detail(id) }),
        queryClient.invalidateQueries({ queryKey: onboardingKeys.audit(id) }),
      ]);
    },
  });
}
